import {css, useTheme} from '@mui/material';
import {
  ConfigObjectType,
  GetStylesFunction,
  IconHeaderWithSearch,
  LightSplitLayout,
  MaximizeButton,
  mixins,
  NoDataMessageWithIcon,
  useLocalStorage,
} from '@wallaby-ui/core';
import {
  ChartColor,
  DEFAULT_PAGE_SIZE,
  getDefaultMTOptions,
  getTableMaxHeight,
  LightChart,
  LightChartLegend2,
  LightSelectedFilters,
  MaterialTableWrapper,
  TableSorting,
  useFormatters,
} from '@wallaby-ui/document';
import {isEqual} from 'lodash';
import * as React from 'react';
import {useCallback, useRef, useState, useMemo, useEffect} from 'react';

import {
  ChartTypeType,
  ChartValueType,
  DEFAULT_DEPARTMENT_REPORT_CHART_TYPE,
  FiltersData,
  getChartSplitLayoutId,
  InterventionsDepartmentReportDto,
  ReportType,
  reportTypeToChartTableLabels,
  reportTypeToTableOps,
} from './departmenttypes';
import {CHARTS_LAYOUT_SPLIT_CONFIG} from './types';
import {reportTypeToReportChartPropMap, useDepartmentReportChartCategories} from './useDepartmentReportChartCategories';
import {useDepartmentReportChartConfig} from './useDepartmentReportChartConfig';
import {useDepartmentReportChartData} from './useDepartmentReportChartData';
import {useDepartmentReportTableColumns} from './useDepartmentReportTableColumns';
import {useDepartmentReportTableData} from './useDepartmentReportTableData';

const useWhyDidYouUpdate = (name: string, props: Record<string, any>) => {
  const previous = useRef<Record<string, any>>();

  useEffect(() => {
    if (previous.current) {
      const allKeys = Object.keys({...previous.current, ...props});
      const changedKeys: Record<string, {from: any; to: any}> = {};

      allKeys.forEach((key) => {
        if (previous.current![key] !== props[key]) {
          changedKeys[key] = {
            from: previous.current![key],
            to: props[key],
          };
        }
      });

      if (Object.keys(changedKeys).length > 0) {
        console.log('[why-did-you-update]', name, changedKeys);
      }
    }

    previous.current = props;
  });
};

export const getTableDefaultSorting = (reportType: ReportType) => reportTypeToTableOps[reportType].defaultSorting;
export const getTableSortingProp = (reportType: ReportType, filters: FiltersData) =>
  reportTypeToTableOps[reportType].getSortingProp(filters);
export const getTableSortingUpdate = (reportType: ReportType, newSorting: TableSorting) =>
  reportTypeToTableOps[reportType].getSortingUpdate(newSorting);

type DepartmentReportChartTableProps = {
  reportType: ReportType;
  data: InterventionsDepartmentReportDto | undefined;
  isLoading: boolean;
  sorting: TableSorting;
  onSortingChange: (reportType: ReportType, sorting: TableSorting) => void;
  storageIdPrefix: string;
  chartValueSelection: ChartValueType | undefined;
  chartTypeSelection: ChartTypeType | undefined;
  config: ConfigObjectType;
};

export const DepartmentReportChartTable: React.FC<DepartmentReportChartTableProps> = ({
  reportType,
  data,
  isLoading,
  sorting,
  onSortingChange,
  storageIdPrefix,
  chartValueSelection,
  chartTypeSelection,
  config,
}) => {
  // useWhyDidYouUpdate('DepartmentReportChartTable', {
  //   reportType,
  //   data,
  //   isLoading,
  //   sorting,
  //   onSortingChange,
  //   storageIdPrefix,
  //   chartValueSelection,
  //   chartTypeSelection,
  //   config,
  // });

  const [chartMaximized, setChartMaximized] = useState(false);
  const [tableMaximized, setTableMaximized] = useState(false);
  const theme = useTheme();

  const [_, setPlanRerender] = useState(0);

  const styleParams = useMemo(
    () => ({
      chartMaximized,
      tableMaximized,
      isLoading,
    }),
    [chartMaximized, tableMaximized, isLoading]
  );

  const muiStyles = useMemo(() => getStyles({theme, params: styleParams}), [theme, styleParams]);

  const {t} = useFormatters();
  const [defaultTableScroll, setDefaultTableScroll] = useLocalStorage(`${storageIdPrefix}.${reportType}.tableScroll`, 0);

  const tableData = useDepartmentReportTableData(reportType, data);
  const categoriesOrder = useMemo(() => (tableData || []).map((data) => data.category), [tableData]);
  const {getCategories, getCategoryLabel} = useDepartmentReportChartCategories(reportType, data, config, categoriesOrder);

  const categoryLabelFunction = useCallback(
    (data: any) => {
      return getCategoryLabel(data.category);
    },
    [getCategoryLabel]
  );

  const tableColumns = useDepartmentReportTableColumns(
    reportType,
    t(reportTypeToChartTableLabels[reportType].categoryColumnTitle),
    categoryLabelFunction,
    sorting
  );

  const chartColorHiddenMap = useRef({} as Record<string | number, boolean>);
  const onSetHiddenMap = useCallback(() => {
    setPlanRerender((v) => v + 1);
  }, []);

  const chartColors: ChartColor[] = getCategories().map((category) => ({
    id: category.category,
    label: category.label,
    color: category.color,
    fillColor: category.fillColor,
    hidden: chartColorHiddenMap.current ? chartColorHiddenMap.current[category.category] ?? false : false,
  }));

  const reportTypeChartTitle = reportTypeToChartTableLabels[reportType].chartTitle;

  const chartType = chartTypeSelection ?? DEFAULT_DEPARTMENT_REPORT_CHART_TYPE;
  const originalChartData = data?.[reportTypeToReportChartPropMap[reportType]]?.charts ?? [];
  const chartData = useDepartmentReportChartData(reportType, data, chartValueSelection, chartType, chartColors);
  const {chartLayout, chartImageDownload} = useDepartmentReportChartConfig(chartType, reportTypeChartTitle);

  const handleOrderChange = useCallback(
    (colIndex: number, direction: 'asc' | 'desc') => {
      const columnId = tableColumns[colIndex].field as string;
      onSortingChange(reportType, {
        field: columnId,
        direction,
      });
    },
    [onSortingChange, reportType, tableColumns]
  );

  const chartSplitLayout = useMemo(() => CHARTS_LAYOUT_SPLIT_CONFIG(getChartSplitLayoutId(reportType)), [reportType]);

  const noDataComponent = useMemo(() => <NoDataMessageWithIcon message={'chart.emptyData'} />, []);

  const handleResetSorting = useCallback(() => {
    onSortingChange(reportType, reportTypeToTableOps[reportType].defaultSorting);
  }, [onSortingChange, reportType]);

  const hasSorting = useMemo(() => !isEqual(sorting, reportTypeToTableOps[reportType].defaultSorting), [sorting, reportType]);

  return (
    <LightSplitLayout
      initSizes={chartSplitLayout[0].sizes}
      minSizes={chartSplitLayout[0].minSize}
      storageId={chartSplitLayout[0].layoutId}
      isVertical={false}
      gutterSize={18}
      noGrow={[false, false]}
      hidden={[false, false]}
    >
      <div css={muiStyles.chartContentBoxOuter}>
        <div css={muiStyles.chartContentBoxInner}>
          <IconHeaderWithSearch
            iconName={'analytics1'}
            label={reportTypeChartTitle}
            useFixedViewBox='0 -1 16 14'
            iconCss={muiStyles.graphIcon}
          >
            <MaximizeButton maximized={chartMaximized} toggleMaximized={() => setChartMaximized(!chartMaximized)} />
          </IconHeaderWithSearch>
          <div css={muiStyles.chartLegendChartBox}>
            <LightChartLegend2
              items={chartColors}
              hiddenMap={chartColorHiddenMap}
              onSetHiddenMap={onSetHiddenMap}
              legendRowStyles={{marginLeft: 'auto'}}
              textColor={'black'}
            />
            <LightChart
              key={JSON.stringify(chartLayout.xaxis.range)}
              data={chartData}
              layout={chartLayout}
              config={{}}
              imageDownload={chartImageDownload}
              isLoading={isLoading}
              chartStyles={{overflow: 'auto'}}
              noDataComponent={noDataComponent}
              originalDataLength={originalChartData.length}
            />
          </div>
        </div>
      </div>
      <div css={muiStyles.tableContentBoxOuter}>
        <div css={muiStyles.tableContentBoxInner}>
          <IconHeaderWithSearch
            iconName={'listIconMultiColor'}
            label={reportTypeToChartTableLabels[reportType].tableTitle}
            useFixedViewBox='0 -1 16 14'
            iconCss={muiStyles.headerIcon}
            childrenWrapperStyle={{gap: 6}}
          >
            <LightSelectedFilters hasSorting={hasSorting} handleResetSorting={handleResetSorting} />
            <MaximizeButton maximized={tableMaximized} toggleMaximized={() => setTableMaximized(!tableMaximized)} />
          </IconHeaderWithSearch>
          <MaterialTableWrapper
            style={{maxHeight: '100%'}}
            defaultScroll={{to: defaultTableScroll}}
            onScroll={(event) => setDefaultTableScroll((event.target as HTMLDivElement).scrollTop)}
            singleLineColumnLabels
            options={{
              ...getDefaultMTOptions(DEFAULT_PAGE_SIZE),
              paging: false,
            }}
            variant='big'
            totalRow='top'
            useMaxHeight
            columns={tableColumns}
            data={tableData}
            isLoading={isLoading}
            totalCount={tableData.length}
            onOrderChange={handleOrderChange}
            controlledSort={sorting}
            centerLoadingSpinner
          />
        </div>
      </div>
    </LightSplitLayout>
  );
};

export const MemoizedDepartmentReportChartTable = React.memo(DepartmentReportChartTable, (prevProps, nextProps) => {
  const changedProps = Object.keys(prevProps).filter((key) => {
    const propKey = key as keyof DepartmentReportChartTableProps;
    return prevProps[propKey] !== nextProps[propKey];
  });

  if (changedProps.length > 0) {
    console.log(`Re-render triggered by:`, changedProps);
    return false;
  }

  return true;
});

const getStyles: GetStylesFunction = (options) => ({
  contentWrapper: css`
    width: 100%;
    height: 100%;
    padding: 16px 16px 16px 0;
  `,
  contentBox: css`
    ${mixins.contentBox};
    border-radius: ${options?.theme?.borderRadius};
    background: #ffffff;
    padding: 16px;
    row-gap: 16px;
    overflow-y: auto;
  `,
  chartsContentBox: css`
    ${mixins.contentBox};
    padding: 0;
    row-gap: 16px;
    overflow-y: auto;
  `,
  chartContentBoxOuter: css`
    ${mixins.contentBox};
    border-radius: ${options?.params?.chartMaximized ? 0 : options?.theme?.borderRadius};
    background: ${options?.params?.chartMaximized ? 'rgb(232, 234, 238)' : '#ffffff'};
    padding: ${options?.params?.chartMaximized ? '16px' : 0};
    padding-left: 0;
    overflow-y: auto;
    position: ${options?.params?.chartMaximized ? 'absolute' : 'static'};
    top: ${options?.params?.chartMaximized ? 0 : 'auto'};
    left: ${options?.params?.chartMaximized ? 0 : 'auto'};
    width: 100%;
    height: 100%;
    z-index: ${options?.params?.chartMaximized ? '100' : 'auto'};
  `,
  chartContentBoxInner: css`
    ${mixins.contentBox};
    border-radius: ${options?.params?.chartMaximized ? options?.theme?.borderRadius : 0};
    background: #ffffff;
    padding: 16px;
    overflow-y: auto;
    width: 100%;
    height: 100%;
  `,
  tableContentBoxOuter: css`
    ${mixins.contentBox};
    border-radius: ${options?.params?.tableMaximized ? 0 : options?.theme?.borderRadius};
    background: ${options?.params?.tableMaximized ? 'rgb(232, 234, 238)' : '#ffffff'};
    padding: ${options?.params?.tableMaximized ? '16px' : 0};
    padding-left: 0;
    overflow-y: auto;
    position: ${options?.params?.tableMaximized ? 'absolute' : 'static'};
    top: ${options?.params?.tableMaximized ? 0 : 'auto'};
    left: ${options?.params?.tableMaximized ? 0 : 'auto'};
    width: 100%;
    height: 100%;
    z-index: ${options?.params?.tableMaximized ? '100' : 'auto'};
  `,
  tableContentBoxInner: css`
    ${mixins.contentBox};
    border-radius: ${options?.params?.tableMaximized ? options?.theme?.borderRadius : 0};
    background: #ffffff;
    padding: 16px;
    overflow-y: auto;
    width: 100%;
    height: 100%;
  `,
  chartLegendChartBox: css`
    ${mixins.contentBox};
    border-radius: 8px;
    background: #ffffff;
    margin-top: 5px;
    flex-grow: ${options?.params?.isLoading ? 1 : 0};
  `,
  headerIcon: css`
    width: 16px;
    height: 16px;
  `,
  graphIcon: css`
    width: 25px;
    height: 30px;
  `,
});
